import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AIPlayerSenderType } from '@/constants/chat'
import { AIPlayerMessage } from '@/redux/types/playerChatbot'

function normalizeSenderType(raw: string | undefined): AIPlayerSenderType {
  const s = String(raw || '').toLowerCase()
  if (s === AIPlayerSenderType.Bot) return AIPlayerSenderType.Bot
  if (s === AIPlayerSenderType.Player) return AIPlayerSenderType.Player
  return AIPlayerSenderType.Player
}

type AIPlayerChatState = {
  connected: boolean
  threadId: number | null
  status: string | null
  messages: AIPlayerMessage[]
  unreadCount: number
  hasMore: boolean
  loading: boolean
  error: string | null
  teamAssignment: {
    botId: number
    botName: string
    teamId: number
    teamName: string
  } | null
}

const initialState: AIPlayerChatState = {
  connected: false,
  threadId: null,
  status: null,
  messages: [],
  unreadCount: 0,
  hasMore: false,
  loading: false,
  error: null,
  teamAssignment: null,
}

const aiPlayerChatSlice = createSlice({
  name: 'aiPlayerChat',
  initialState,
  reducers: {
    setConnectionStatus(state, action: PayloadAction<boolean>) {
      state.connected = action.payload
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },
    setThread(state, action: PayloadAction<any>) {
      const incoming: AIPlayerMessage[] = action.payload.messages || []
      const merged: AIPlayerMessage[] = incoming.map((m) => ({
        ...m,
        sender_type: normalizeSenderType(m.sender_type as string),
      }))
      for (const m of state.messages) {
        if (m.id < 0) {
          const hasServerCopy = incoming.some(
            (i) =>
              i.content === m.content &&
              normalizeSenderType(i.sender_type as string) ===
                AIPlayerSenderType.Player
          )
          if (!hasServerCopy) merged.push(m)
        }
      }
      merged.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )
      state.threadId = action.payload.thread_id
      state.status = action.payload.status
      state.messages = merged
      state.hasMore = !!action.payload.pagination?.has_more
    },
    prependMessages(state, action: PayloadAction<AIPlayerMessage[]>) {
      state.messages = [...action.payload, ...state.messages]
    },
    addMessage(state, action: PayloadAction<AIPlayerMessage>) {
      const incoming = action.payload
      if (incoming.id > 0) {
        state.messages = state.messages.filter(
          (m) =>
            !(
              m.id < 0 &&
              m.sender_type === AIPlayerSenderType.Player &&
              m.content === incoming.content
            )
        )
      }
      if (!state.messages.find((m) => m.id === incoming.id)) {
        state.messages.push({
          ...incoming,
          sender_type: normalizeSenderType(incoming.sender_type as string),
        })
      }
    },
    removeMessageById(state, action: PayloadAction<number>) {
      state.messages = state.messages.filter((m) => m.id !== action.payload)
    },
    incrementUnread(state) {
      state.unreadCount += 1
    },
    clearUnread(state) {
      state.unreadCount = 0
    },
    setTeamAssignment(
      state,
      action: PayloadAction<{
        botId: number
        botName: string
        teamId: number
        teamName: string
      } | null>
    ) {
      state.teamAssignment = action.payload
    },
    clearState() {
      return initialState
    },
  },
})

export const {
  setConnectionStatus,
  setError,
  setLoading,
  setThread,
  prependMessages,
  addMessage,
  removeMessageById,
  incrementUnread,
  clearUnread,
  setTeamAssignment,
  clearState,
} = aiPlayerChatSlice.actions

export default aiPlayerChatSlice.reducer
